import SEO from "@/components/SEO";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import Breadcrumb from "@/components/Breadcrumb";
import InstantQuoteCTA from "@/components/InstantQuoteCTA";
import { Button } from "@/components/ui/button";
import { Phone, ArrowRight, Mail } from "lucide-react";
import { Link } from "react-router-dom";
import weddingCoupleImg from "@/assets/wedding-couple.jpg";
import weddingCoupleVideo from "@/assets/wedding-couple-cinematic.mp4.asset.json";

const WeddingTransportation = () => {
  const serviceSchema = {
    "@context": "https://schema.org",
    "@type": "Service",
    "@id": "https://ricksblackcarservicesantonio.com/wedding-transportation#service",
    "name": "Wedding Transportation San Antonio",
    "alternateName": "Luxury Chauffeur Service",
    "serviceType": "Wedding Transportation",
    "provider": {
      "@type": "LocalBusiness",
      "@id": "https://ricksblackcarservicesantonio.com/#business",
      "name": "Rick's Black Car Service",
      "telephone": "+1-210-296-7409",
      "openingHours": "Mo-Su 00:00-23:59"
    },
    "areaServed": {
      "@type": "City",
      "name": "San Antonio",
      "containedInPlace": {
        "@type": "State",
        "name": "Texas"
      }
    },
    "description": "Elegant wedding transportation in San Antonio & the Texas Hill Country. Bride & groom, bridal party, and guest shuttles. 5-star rated. Call 210-296-7409.",
    "image": [
      "https://ricksblackcarservicesantonio.com/og-image.jpg"
    ],
    "offers": {
      "@type": "AggregateOffer",
      "url": "https://ricksblackcarservicesantonio.com/wedding-transportation",
      "priceCurrency": "USD",
      "lowPrice": "110",
      "highPrice": "540",
      "offerCount": "1",
      "priceValidUntil": "2026-12-31",
      "availability": "https://schema.org/InStock"
    },
    "priceRange": "$110-$540"
  };

  return (
    <>
      <SEO
        title="Wedding Transportation San Antonio | Luxury Chauffeur Service | Rick's"
        description="Elegant wedding transportation in San Antonio & the Texas Hill Country. Bride & groom, bridal party, and guest shuttles. 5-star rated. Call 210-296-7409."
        canonicalPath="/wedding-transportation"
        schema={serviceSchema}
        includeBreadcrumbs={false}
      />

      <div className="min-h-screen">
        <Navbar />
        <main className="pt-20">
          <Breadcrumb items={[{ name: "Wedding Transportation", url: "/wedding-transportation" }]} />
          {/* Hero Section */}
          <section className="relative min-h-[60vh] overflow-hidden flex items-center justify-center">
            <div className="absolute inset-0 z-0">
              <video
                src={weddingCoupleVideo.url}
                poster={weddingCoupleImg}
                autoPlay
                loop
                muted
                playsInline
                preload="metadata"
                disableRemotePlayback
                aria-label="Elegant wedding transportation in San Antonio"
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-b from-background/30 via-background/40 to-background/60" />
            </div>
            <div className="container mx-auto px-4 relative z-10 text-center">
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-serif font-bold text-foreground mb-6">
                Wedding Transportation in San Antonio & the Texas Hill Country
              </h1>
              <p className="text-lg md:text-xl text-muted-foreground mb-8 max-w-3xl mx-auto">
                Elegant, meticulously punctual wedding transportation throughout San Antonio and the Hill Country.
              </p>
              <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
                <Button className="btn-luxury text-lg px-8 py-6" asChild>
                  <a href="tel:+12102967409">
                    <Phone className="w-5 h-5 mr-2" />
                    Call 210-296-7409
                  </a>
                </Button>
                <Button className="btn-luxury text-lg px-8 py-6" asChild>
                  <a href="https://ricksblackcarservicesantonio.square.site/" target="_blank" rel="noopener noreferrer">
                    Book Online Now
                    <ArrowRight className="w-5 h-5 ml-2" />
                  </a>
                </Button>
              </div>
            </div>
          </section>

          {/* Main Content */}
          <section className="py-20 luxury-gradient">
            <div className="container mx-auto px-4">
              <div className="max-w-4xl mx-auto">
                <div className="prose prose-lg max-w-none">
                  <p className="text-muted-foreground text-lg leading-relaxed mb-12">
                    Your wedding day runs on a timeline — and nothing derails a timeline faster than transportation that shows up late, gets lost, or arrives in a car that doesn't match the occasion. Rick's Black Car Service provides elegant, meticulously punctual wedding transportation throughout San Antonio and the Hill Country, so the only thing you think about on your big day is each other.
                  </p>

                  <h2 className="text-3xl md:text-4xl font-serif font-bold text-foreground mb-4">
                    Chauffeur Service for the Couple, the Party, and the Guests
                  </h2>
                  <p className="text-muted-foreground text-lg leading-relaxed mb-10">
                    We provide luxury transportation for every part of your celebration: the bride and groom's grand arrival and getaway, bridal party shuttles between getting-ready locations and the venue, transportation for parents and grandparents, and coordinated guest shuttles between hotels and the reception. One point of contact, one seamless schedule.
                  </p>

                  <h2 className="text-3xl md:text-4xl font-serif font-bold text-foreground mb-4">
                    A Vehicle Worthy of the Photos
                  </h2>
                  <p className="text-muted-foreground text-lg leading-relaxed mb-10">
                    Our 2025 Audi Q7 Lux Black SUV is detailed to perfection before every wedding — a sophisticated, modern backdrop for your arrival photos and a comfortable, climate-controlled retreat between venues. No tacky party-bus vibes; just clean, timeless luxury that complements your celebration.
                  </p>

                  <h2 className="text-3xl md:text-4xl font-serif font-bold text-foreground mb-4">
                    We Work With Your Planner and Your Timeline
                  </h2>
                  <p className="text-muted-foreground text-lg leading-relaxed mb-10">
                    Weddings don't run on approximations. We coordinate directly with your wedding planner or day-of coordinator to build a minute-by-minute transportation schedule — ceremony arrival, cocktail hour transfers, reception send-off — with built-in buffers so no one is ever waiting. Military precision isn't a slogan here; the owner is a 22-year U.S. Army veteran, and it shows in the execution.
                  </p>

                  <h2 className="text-3xl md:text-4xl font-serif font-bold text-foreground mb-4">
                    Serving San Antonio's Most Beautiful Venues
                  </h2>
                  <p className="text-muted-foreground text-lg leading-relaxed mb-10">
                    We drive weddings across the entire region: San Antonio venues from the Pearl District to The Dominion, Hill Country favorites in Boerne and New Braunfels, <Link to="/wine-tours" className="text-primary hover:underline">vineyard and ranch venues around Fredericksburg</Link>, and everywhere in between. If your ceremony and reception are in different towns, we handle the logistics.
                  </p>

                  <h2 className="text-3xl md:text-4xl font-serif font-bold text-foreground mb-4">
                    Book Early — Wedding Season Fills Fast
                  </h2>
                  <p className="text-muted-foreground text-lg leading-relaxed mb-12">
                    Spring and fall weekends book out weeks in advance. We recommend reserving your wedding transportation 2–4 weeks ahead minimum, and earlier for peak season dates. A quick call locks in your date, your vehicle, and your rate.
                  </p>
                </div>

                {/* Bottom CTA */}
                <div className="text-center py-12 border-t border-border/50">
                  <p className="text-xl md:text-2xl text-foreground font-semibold mb-6">
                    Get a wedding transportation quote today — call 210-296-7409 or email ricksblackcarservice@outlook.com. Tell us your date and venues, and we'll build the schedule.
                  </p>
                  <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
                    <Button className="btn-luxury text-lg px-8 py-6" asChild>
                      <a href="tel:+12102967409">
                        <Phone className="w-5 h-5 mr-2" />
                        Call 210-296-7409
                      </a>
                    </Button>
                    <Button className="btn-luxury text-lg px-8 py-6" asChild>
                      <a href="mailto:ricksblackcarservice@outlook.com">
                        <Mail className="w-5 h-5 mr-2" />
                        Email Us
                      </a>
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          </section>

          <InstantQuoteCTA />
        </main>
        <Footer />
      </div>
    </>
  );
};

export default WeddingTransportation;
